import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  fetchCxPublicRating,
  findRuleForStars,
  isRatingFormComplete,
  submitCxPublicRating,
} from '../services/cxPublicRatingApi';
import './CxPublicRatingPage.css';

function formatSerie(numSerie, pat) {
  const serie = String(numSerie || '').trim();
  const tag = String(pat || '').trim();
  if (!serie && !tag) return '—';
  if (!tag) return serie;
  if (!serie) return tag;
  return `${serie} · ${tag}`;
}

function StarRating({ value, onChange, disabled }) {
  const [hover, setHover] = useState(null);
  const display = hover ?? value ?? 0;

  return (
    <div className="cx-public-rating-stars" role="radiogroup" aria-label="Sua avaliação">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          className={`cx-public-rating-star-btn${display >= n ? ' is-active' : ''}`}
          onClick={() => !disabled && onChange(n)}
          onMouseEnter={() => !disabled && setHover(n)}
          onMouseLeave={() => !disabled && setHover(null)}
          disabled={disabled}
          aria-label={`${n} estrela${n > 1 ? 's' : ''}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

function GroupedOsList({ orders }) {
  return (
    <div className="cx-public-rating-grouped-os">
      <p className="cx-public-rating-grouped-os-label">Ordens de serviço incluídas</p>
      <ul className="cx-public-rating-os-chips">
        {orders.map((order) => (
          <li key={order.osNum} className="cx-public-rating-os-chip">
            OS {order.osNum}
            {order.tipoOs && order.tipoOs !== '—' ? (
              <span className="cx-public-rating-os-chip-type">{order.tipoOs}</span>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="cx-public-rating-grouped-hint">
        Sua avaliação será aplicada a todas as ordens listadas acima.
      </p>
    </div>
  );
}

function OrderDetails({ order, compact = false }) {
  return (
    <div className={`cx-public-rating-order${compact ? ' cx-public-rating-order--compact' : ''}`}>
      <div className="cx-public-rating-order-head">
        <h3 className="cx-public-rating-order-title">
          OS {order.osNum}
          {order.tipoOs && order.tipoOs !== '—' ? ` · ${order.tipoOs}` : ''}
        </h3>
        <p className="cx-public-rating-order-sub">
          Atendimento em {order.dataAtendimento || '—'}
          {order.tecnico && order.tecnico !== '—' ? ` · ${order.tecnico}` : ''}
        </p>
      </div>
      <table className="cx-public-rating-order-table">
        <tbody>
          <tr>
            <td className="cx-public-rating-order-label">Equipamento</td>
            <td className="cx-public-rating-order-value">{order.equipamento || '—'}</td>
          </tr>
          <tr>
            <td className="cx-public-rating-order-label">Nº série</td>
            <td className="cx-public-rating-order-value">{formatSerie(order.numSerie, order.pat)}</td>
          </tr>
          {!compact ? (
            <tr>
              <td className="cx-public-rating-order-label">Abertura</td>
              <td className="cx-public-rating-order-value">{order.dataAbertura || '—'}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
      <div className="cx-public-rating-order-details">
        <div className="cx-public-rating-detail-item">
          <span className="cx-public-rating-detail-label">Defeito:</span>
          {order.defeitoDescricao}
          {order.defeitoObs && order.defeitoObs !== '—' ? (
            <>
              {' '}
              <span aria-hidden>|</span>
              {' '}
              {order.defeitoObs}
            </>
          ) : null}
        </div>
        <div className="cx-public-rating-detail-item">
          <span className="cx-public-rating-detail-label">Condição:</span>
          {order.condicao}
          {order.laudo && order.laudo !== '—' ? (
            <>
              {' '}
              <span aria-hidden>|</span>
              {' '}
              {order.laudo}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function RatingForm({
  formKey,
  orders = [],
  batch = false,
  rules,
  token,
  onRated,
}) {
  const [stars, setStars] = useState(null);
  const [motivId, setMotivId] = useState(null);
  const [motivDetail, setMotivDetail] = useState('');
  const [generalNote, setGeneralNote] = useState('');
  const [noteOnMotive, setNoteOnMotive] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const pendingOrders = useMemo(
    () => (orders || []).filter((o) => !o.rated),
    [orders]
  );
  const allRated = pendingOrders.length === 0 && (orders || []).length > 0;

  const rule = useMemo(() => findRuleForStars(rules, stars), [rules, stars]);
  const selectedMotive = useMemo(
    () => (rule?.motives || []).find((m) => m.id === motivId) || null,
    [rule, motivId]
  );

  useEffect(() => {
    setMotivId(null);
    setMotivDetail('');
    setGeneralNote('');
    setNoteOnMotive('');
    setError('');
  }, [stars]);

  useEffect(() => {
    setMotivDetail('');
    setNoteOnMotive('');
    setError('');
  }, [motivId]);

  const handleSubmit = async () => {
    if (!isFormComplete || submitting || allRated) return;
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        ratingToken: token,
        stars,
        motivId: motivId || undefined,
        motivDetail: motivDetail || undefined,
        generalNote: generalNote || undefined,
        noteOnMotive: noteOnMotive || undefined,
      };

      if (batch || pendingOrders.length > 1) {
        payload.submitAll = true;
      } else {
        payload.osNum = pendingOrders[0]?.osNum || orders[0]?.osNum;
      }

      const result = await submitCxPublicRating(payload);
      const ratedOs = result?.osNums || (result?.osNum ? [result.osNum] : pendingOrders.map((o) => o.osNum));
      onRated?.(ratedOs, result?.stars ?? stars);
    } catch (e) {
      setError(e.message || 'Erro ao enviar avaliação.');
    } finally {
      setSubmitting(false);
    }
  };

  if (allRated) {
    const ratedOrder = (orders || []).find((o) => o.rated);
    return (
      <div className="cx-public-rating-form cx-public-rating-form--done">
        Avaliação registrada
        {ratedOrder?.stars ? ` · ${ratedOrder.stars} ★` : ''}
      </div>
    );
  }

  const showMotives = Boolean(rule?.askReason && (rule.motives || []).length);
  const showMotivDetail = Boolean(motivId && selectedMotive?.requireDetail);
  const showGeneralNote = Boolean(rule?.requireNote);
  const showNoteOnMotive = Boolean(motivId && rule?.requireNoteOnMotive);
  const showMotivePanel = showMotives || showGeneralNote || showMotivDetail || showNoteOnMotive;
  const isFormComplete = isRatingFormComplete({
    stars,
    rule,
    motivId,
    selectedMotive,
    motivDetail,
    generalNote,
    noteOnMotive,
  });
  const formId = formKey || 'default';

  return (
    <div className={`cx-public-rating-form${batch ? ' cx-public-rating-form--grouped' : ''}`}>
      <div className="cx-public-rating-form-scroll">
        <span className="cx-public-rating-form-label">
          {batch ? 'Sua avaliação para todas as ordens' : 'Sua avaliação'}
        </span>
        <StarRating value={stars} onChange={setStars} disabled={submitting} />

        {stars && showMotivePanel ? (
          <div className="cx-public-rating-panel">
            {showMotives ? (
              <>
                <p className="cx-public-rating-panel-title">Motivo da avaliação</p>
                <ul className="cx-public-rating-motives">
                  {(rule.motives || []).map((motive) => (
                    <li key={motive.id}>
                      <label
                        className={`cx-public-rating-motive${motivId === motive.id ? ' is-selected' : ''}`}
                      >
                        <input
                          type="radio"
                          name={`motiv-${formId}`}
                          checked={motivId === motive.id}
                          onChange={() => setMotivId(motive.id)}
                          disabled={submitting}
                        />
                        <span>{motive.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {showMotivDetail ? (
              <div className="cx-public-rating-field">
                <label htmlFor={`motiv-detail-${formId}`}>Observação sobre o motivo</label>
                <textarea
                  id={`motiv-detail-${formId}`}
                  value={motivDetail}
                  onChange={(e) => setMotivDetail(e.target.value)}
                  placeholder="Informações adicionais sobre o motivo"
                  disabled={submitting}
                />
              </div>
            ) : null}

            {showNoteOnMotive ? (
              <div className="cx-public-rating-field">
                <label htmlFor={`note-motive-${formId}`}>Observação ao motivo</label>
                <textarea
                  id={`note-motive-${formId}`}
                  value={noteOnMotive}
                  onChange={(e) => setNoteOnMotive(e.target.value)}
                  placeholder="Comentários sobre o motivo selecionado"
                  disabled={submitting}
                />
              </div>
            ) : null}

            {showGeneralNote ? (
              <div className="cx-public-rating-field">
                <label htmlFor={`general-note-${formId}`}>Observação geral</label>
                <textarea
                  id={`general-note-${formId}`}
                  value={generalNote}
                  onChange={(e) => setGeneralNote(e.target.value)}
                  placeholder="Comentários gerais sobre o atendimento"
                  disabled={submitting}
                />
              </div>
            ) : null}
          </div>
        ) : null}

        {error ? <p className="cx-public-rating-error" role="alert">{error}</p> : null}

        {stars && !isFormComplete ? (
          <p className="cx-public-rating-form-hint">
            Complete os campos obrigatórios para habilitar o envio.
          </p>
        ) : null}
      </div>

      {isFormComplete ? (
        <button
          type="button"
          className="cx-public-rating-submit"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? 'Enviando…' : batch ? 'Enviar avaliação das ordens' : 'Enviar avaliação'}
        </button>
      ) : null}
    </div>
  );
}

export default function CxPublicRatingPage() {
  const location = useLocation();
  const token = useMemo(() => {
    const params = new URLSearchParams(location.search || '');
    return params.get('token') || '';
  }, [location.search]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pageData, setPageData] = useState(null);

  const load = useCallback(async () => {
    if (!token) {
      setError('Link inválido. Verifique o endereço recebido por e-mail.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchCxPublicRating(token);
      setPageData(data);
    } catch (e) {
      setError(e.message || 'Não foi possível carregar a avaliação.');
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    document.body.classList.add('login-novo-active');
    return () => document.body.classList.remove('login-novo-active');
  }, []);

  const handleRated = useCallback((ratedOsNums, stars) => {
    const osSet = new Set((ratedOsNums || []).map((n) => String(n)));
    setPageData((prev) => {
      if (!prev) return prev;
      const orders = (prev.orders || []).map((o) =>
        osSet.has(String(o.osNum))
          ? { ...o, rated: true, stars: stars ?? o.stars }
          : o
      );
      const allRated = orders.every((o) => o.rated);
      return {
        ...prev,
        orders,
        status: allRated ? 'completed' : prev.status,
        message: allRated ? 'Obrigado! Sua avaliação já foi registrada.' : prev.message,
      };
    });
  }, []);

  const isCompleted = pageData?.status === 'completed';
  const empresa = pageData?.empresa || {};
  const orders = pageData?.orders || [];
  const isGrouped = Boolean(pageData?.isGrouped) || orders.length > 1;

  return (
    <div className={`cx-public-rating-page${isGrouped ? ' cx-public-rating-page--grouped' : ''}`}>
      <div className="cx-public-rating-shell">
        <div className="cx-public-rating-card">
          <header className="cx-public-rating-header">
            {empresa.logoUrl ? (
              <img
                src={empresa.logoUrl}
                alt={empresa.nome || 'Empresa'}
                className="cx-public-rating-logo"
              />
            ) : empresa.nome ? (
              <p className="cx-public-rating-company">{empresa.nome}</p>
            ) : null}
          </header>

          {loading ? (
            <div className="cx-public-rating-state">
              <Loader2 size={22} className="ec-filters-refresh-btn__icon--spin" aria-hidden />
              <p>Carregando avaliação…</p>
            </div>
          ) : error ? (
            <div className={`cx-public-rating-state cx-public-rating-state--error`} role="alert">
              {error}
            </div>
          ) : (
            <>
              <div className="cx-public-rating-intro">
                <h1>Avaliar atendimento</h1>
                <p>
                  {pageData?.solicitante && pageData.solicitante !== '—'
                    ? `Olá, ${pageData.solicitante}! `
                    : ''}
                  {isGrouped
                    ? 'Confira os atendimentos ao lado e registre uma única avaliação para todas as ordens.'
                    : 'Sua opinião nos ajuda a melhorar continuamente nosso serviço.'}
                </p>
              </div>

              {isCompleted ? (
                <div className="cx-public-rating-body">
                  <p className="cx-public-rating-completed" role="status">
                    {pageData?.message || 'Obrigado! Sua avaliação já foi registrada.'}
                  </p>
                  {isGrouped ? <GroupedOsList orders={orders} /> : null}
                  {orders.map((order) => (
                    <OrderDetails key={order.osNum} order={order} compact={isGrouped} />
                  ))}
                </div>
              ) : isGrouped ? (
                <>
                  <div className="cx-public-rating-grouped-banner">
                    <GroupedOsList orders={orders} />
                  </div>
                  <div className="cx-public-rating-grouped-layout">
                    <aside className="cx-public-rating-grouped-orders" aria-label="Detalhes dos atendimentos">
                      <p className="cx-public-rating-details-heading">Detalhes dos atendimentos</p>
                      <div className="cx-public-rating-grouped-orders-scroll">
                        {orders.map((order) => (
                          <OrderDetails key={order.osNum} order={order} compact />
                        ))}
                      </div>
                    </aside>
                    <section className="cx-public-rating-grouped-form" aria-label="Formulário de avaliação">
                      <RatingForm
                        formKey="grouped"
                        batch
                        orders={orders}
                        rules={pageData?.rules || []}
                        token={token}
                        onRated={handleRated}
                      />
                    </section>
                  </div>
                </>
              ) : (
                <div className="cx-public-rating-body">
                  {orders.map((order) => (
                    <div key={order.osNum}>
                      <OrderDetails order={order} />
                      <RatingForm
                        formKey={order.osNum}
                        orders={[order]}
                        rules={pageData?.rules || []}
                        token={token}
                        onRated={handleRated}
                      />
                    </div>
                  ))}
                </div>
              )}

              <footer className="cx-public-rating-footer">
                Este link é pessoal e válido por tempo limitado.
              </footer>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
